import React, { useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { ProjectModule, ProjectStatus } from "../types";
import { formatDate, formatText } from "../../../utils/utils";
import { tableHeaderStyles, tableCellStyles } from "../../../utils/styles";
import { PROJECT_TERMINOLOGY } from "../enums";
import { UserScope } from "../../../types/operationType";

export interface ModuleListViewProps {
  modules: ProjectModule[];
  onView: (module: ProjectModule) => void;
  onEdit: (module: ProjectModule) => void;
  onDelete: (id: string) => void;
  userScope: UserScope;
}

const ModuleListView: React.FC<ModuleListViewProps> = ({
  modules,
  onView,
  onEdit,
  onDelete,
  userScope,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedModule, setSelectedModule] = useState<ProjectModule | null>(
    null
  );

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    module: ProjectModule
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedModule(module);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedModule(null);
  };

  const handleEdit = () => {
    if (selectedModule) {
      onEdit(selectedModule);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedModule) {
      onDelete(selectedModule.id);
    }
    handleMenuClose();
  };

  if (modules.length === 0) {
    return (
      <Paper sx={{ mt: 2, p: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No {PROJECT_TERMINOLOGY.LOWER_PLURAL} found
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ mt: 2 }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderStyles}>
                {PROJECT_TERMINOLOGY.SINGULAR} Name
              </TableCell>
              {/* <TableCell sx={tableHeaderStyles}>Status</TableCell> */}
              <TableCell sx={tableHeaderStyles}>Client</TableCell>
              <TableCell sx={tableHeaderStyles}>Start Date</TableCell>
              <TableCell sx={tableHeaderStyles}>End Date</TableCell>
              {/* <TableCell sx={tableHeaderStyles}>Created By</TableCell> */}
              {userScope === UserScope.GLOBAL && (
                <TableCell sx={tableHeaderStyles} align="center">
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {modules.map((module) => {
              const isTerminated = module.status === ProjectStatus.TERMINATED;
              return (
                <TableRow key={module.id} hover>
                  <TableCell
                    sx={{
                      ...tableCellStyles,
                      color: isTerminated ? "text.secondary" : "text.primary",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        cursor: "pointer",
                        textDecoration: "underline",
                        fontSize: "0.8rem",
                        color: isTerminated ? "text.secondary" : "text.primary",
                      }}
                      onClick={() => onView(module)}
                    >
                      {module.name}
                    </Typography>
                  </TableCell>
                  {/* <TableCell
                    sx={{
                      ...tableCellStyles,
                      color: isTerminated ? "text.secondary" : "text.primary",
                    }}
                  >
                    {formatText(module.status)}
                  </TableCell> */}
                  <TableCell
                    sx={{
                      ...tableCellStyles,
                      color: isTerminated ? "text.secondary" : "text.primary",
                    }}
                  >
                    {module.client?.clientName}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellStyles,
                      color: isTerminated ? "text.secondary" : "text.primary",
                    }}
                  >
                    {formatDate(module.startDate)}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellStyles,
                      color: isTerminated ? "text.secondary" : "text.primary",
                    }}
                  >
                    {module.endDate ? formatDate(module.endDate) : "-"}
                  </TableCell>
                  {/* <TableCell
                    sx={{
                      ...tableCellStyles,
                      color: isTerminated ? "text.secondary" : "text.primary",
                    }}
                  >
                    {module.createdByUser?.name}
                  </TableCell> */}
                  {userScope === UserScope.GLOBAL && (
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        color: isTerminated ? "text.secondary" : "text.primary",
                      }}
                      align="center"
                    >
                      <Tooltip title="More actions">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, module)}
                        >
                          <MoreVertIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default ModuleListView;
